/**
 * Session-scoped persistence for successful repo edit pushes.
 * Keyed by thread + message + repo so push controls stay hidden after success.
 */

export interface RepoPushRecord {
  branch: string;
  prUrl?: string;
  pushedAt: string;
  files: string[];
  ragUpdated?: boolean;
}

const STORAGE_PREFIX = 'repo-push-state:';

function storageKey(threadId: string): string {
  return `${STORAGE_PREFIX}${threadId}`;
}

/** Panel key used in ChatWorkspace state: `${messageId}:${fullName}` */
export function pushPanelKey(messageId: string, repoFullName: string): string {
  return `${messageId}:${repoFullName}`;
}

export function loadPushedRepoEdits(threadId: string | null | undefined): Record<string, RepoPushRecord> {
  if (!threadId || typeof sessionStorage === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(storageKey(threadId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, RepoPushRecord>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function savePushedRepoEdit(
  threadId: string | null | undefined,
  panelKey: string,
  record: RepoPushRecord
): Record<string, RepoPushRecord> {
  if (!threadId || typeof sessionStorage === 'undefined') {
    return { [panelKey]: record };
  }
  const existing = loadPushedRepoEdits(threadId);
  const next = { ...existing, [panelKey]: record };
  try {
    sessionStorage.setItem(storageKey(threadId), JSON.stringify(next));
  } catch {
    // quota or private mode — in-memory state still works for session
  }
  return next;
}
